class ProductGalleryLuxury {
  constructor(root) {
    this.root = root;
    this.lightbox = root.querySelector('[data-product-lightbox]');
    this.lightboxImage = root.querySelector('[data-lightbox-image]');
    this.closeTargets = root.querySelectorAll('[data-lightbox-close]');
    this.stage = root.querySelector('[data-lightbox-stage]');
    this.zoomTriggers = root.querySelectorAll('[data-gallery-zoom-trigger]');

    this.carousel = root.querySelector('[data-gallery-carousel]');
    this.viewport = root.querySelector('[data-gallery-viewport]');
    this.track = root.querySelector('[data-gallery-track]');
    this.slides = [...root.querySelectorAll('[data-gallery-slide]')];
    this.prevBtn = root.querySelector('[data-gallery-prev]');
    this.nextBtn = root.querySelector('[data-gallery-next]');
    this.counter = root.querySelector('[data-gallery-counter]');

    this.index = 0;
    this.slidesPerView = 1;
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isDragging = false;
    this.suppressClick = false;

    this.bindLightbox();
    this.initCarousel();
  }

  bindLightbox() {
    this.zoomTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.openLightbox(trigger));
    });

    this.closeTargets.forEach((el) => {
      el.addEventListener('click', () => this.closeLightbox());
    });

    if (this.stage) {
      this.stage.addEventListener('click', (event) => {
        if (event.target === this.stage) this.closeLightbox();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeLightbox();
        return;
      }

      if (!this.carousel || this.slides.length <= this.slidesPerView) return;
      if (!this.root.contains(document.activeElement) && document.activeElement !== document.body) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      }
    });
  }

  initCarousel() {
    if (!this.carousel || !this.track || this.slides.length <= 1) return;

    this.updateSlidesPerView();
    this.desktopQuery.addEventListener('change', () => this.updateSlidesPerView());

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    if (this.viewport) {
      this.viewport.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
      this.viewport.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: true });
      this.viewport.addEventListener('touchend', (event) => this.onTouchEnd(event));
    }

    window.addEventListener('resize', () => this.updateTransform(false));

    this.goTo(0, false);
  }

  updateSlidesPerView() {
    const previous = this.slidesPerView;
    this.slidesPerView = this.desktopQuery.matches ? Math.min(2, this.slides.length) : 1;
    this.root.style.setProperty('--gallery-slides-per-view', String(this.slidesPerView));

    if (this.index > this.maxIndex()) {
      this.index = this.maxIndex();
    }

    if (previous !== this.slidesPerView) {
      this.updateTransform(false);
    } else {
      this.updateTransform(false);
    }

    this.updateControls();
  }

  maxIndex() {
    return Math.max(0, this.slides.length - this.slidesPerView);
  }

  step(delta) {
    this.goTo(this.index + delta);
  }

  goTo(index, animate = true) {
    this.index = Math.max(0, Math.min(index, this.maxIndex()));
    this.updateTransform(animate);
    this.updateControls();
  }

  updateTransform(animate = true) {
    if (!this.track || !this.viewport) return;

    const slideWidth = this.viewport.clientWidth / this.slidesPerView;
    this.track.classList.toggle('is-animating', animate);
    this.track.style.transform = `translate3d(-${this.index * slideWidth}px, 0, 0)`;
  }

  updateControls() {
    const atStart = this.index <= 0;
    const atEnd = this.index >= this.maxIndex();

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    if (this.counter) {
      const current = this.index + 1;
      const total =
        this.slidesPerView === 1 ? this.slides.length : Math.max(1, this.maxIndex() + 1);
      const template = this.counter.dataset.counterTemplate || '__CURRENT__ / __TOTAL__';

      this.counter.textContent = template
        .replace('__CURRENT__', String(current))
        .replace('__TOTAL__', String(total));
    }
  }

  onTouchStart(event) {
    if (event.touches.length !== 1) return;

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.isDragging = false;
  }

  onTouchMove(event) {
    if (event.touches.length !== 1) return;

    const deltaX = Math.abs(event.touches[0].clientX - this.touchStartX);
    const deltaY = Math.abs(event.touches[0].clientY - this.touchStartY);

    if (deltaX > deltaY && deltaX > 10) {
      this.isDragging = true;
    }
  }

  onTouchEnd(event) {
    if (!this.isDragging) return;

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;

    if (Math.abs(deltaX) >= 40) {
      this.step(deltaX > 0 ? -1 : 1);
      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 300);
    }

    this.isDragging = false;
  }

  openLightbox(trigger) {
    if (!this.lightbox || !this.lightboxImage || this.suppressClick) return;

    const url = trigger.dataset.imageUrl;
    const alt = trigger.dataset.imageAlt;

    if (!url) return;

    this.lightboxImage.src = url;
    this.lightboxImage.alt = alt || '';
    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

class ProductRecommendations {
  constructor(root) {
    this.root = root;
    this.url = root.dataset.url;

    if (!this.url) return;

    this.load();
  }

  async load() {
    if (this.root.querySelector('.product-cross-sell')) return;

    try {
      const response = await fetch(this.url);
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content =
        doc.querySelector('.product-cross-sell') ||
        doc.querySelector('[data-product-recommendations] .product-cross-sell');

      if (content) {
        this.root.innerHTML = content.outerHTML;
      }
    } catch {
      /* recommendations unavailable */
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-gallery]').forEach((root) => {
    new ProductGalleryLuxury(root);
  });

  document.querySelectorAll('[data-product-recommendations]').forEach((root) => {
    new ProductRecommendations(root);
  });
});
